import React, { useMemo } from "react";
import { useLocation, useHistory } from "react-router-dom";
import {
  Breadcrumbs,
  Link,
  Typography,
  Button,
  Box,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  NavigateNext as NavigateNextIcon,
  ArrowBack as ArrowBackIcon,
  Home as HomeIcon,
} from "@mui/icons-material";

/**
 * AppBreadcrumbs — TASK-015
 * Breadcrumbs contextuais gerados automaticamente a partir da rota.
 * Desktop: breadcrumb completo (Home > Seção > Página)
 * Mobile: botão compacto "← Voltar para X"
 *
 * Props:
 *   - customLabels: objeto { "/rota": "Label" } para sobrescrever labels automáticos
 *   - hideOnRoutes: array de rotas onde esconder (ex: ["/", "/login"])
 *   - maxItems: máximo de items no breadcrumb desktop (default 4)
 */

// Mapa de labels para rotas conhecidas do sistema
const ROUTE_LABELS = {
  "": "Início",
  dashboard: "Dashboard",
  account: "Meu Perfil",
  oxetrilhas: "OxeTrilhas",
  oxetrilha: "Trilha",
  trilha: "Trilha",
  curso: "Curso",
  oxetechlab: "OxeTech Lab",
  oxetechacademy: "OxeTech Academy",
  oxetechedu: "OxeTech Edu",
  oxetechwork: "OxeTech Work",
  oxedin: "OxeDin",
  login: "Login",
  cadastro: "Cadastro",
  "empresa-cadastro": "Cadastro Empresa",
  "select-cadastro": "Tipo de Cadastro",
  "perfil-laboratorio": "Laboratório",
  laboratorio: "Laboratório",
  "turma-selecao-painel": "Seleção de Turma",
  "turma-iniciada-painel": "Painel da Turma",
  "painel-instrutor": "Painel do Instrutor",
  calendario: "Calendário",
  "catalogo-cursos": "Catálogo de Cursos",
  escola: "Escola",
  validar: "Validar Alunos",
  dash: "Dashboard",
  turma: "Turma",
  acompanhamento: "Acompanhamento",
  "live-class": "Aula ao Vivo",
  "sala-de-aula": "Sala de Aula",
  "minhas-turmas-instrutor": "Minhas Turmas",
  dashadmin: "Admin",
  work: "Work",
  administradores: "Administradores",
  alunos: "Alunos",
  aluno: "Perfil do Aluno",
  "oxetechlab-dashboard": "Dashboard Lab",
  "oxetechedu-dashboard": "Dashboard Edu",
  vagas: "Vagas",
  "oxetechwork-inscritos": "Inscritos Work",
  "trilhas-admin": "Admin Trilhas",
  "admin-academy": "Admin Academy",
  "admin-suporte": "Suporte",
  bancotalentos: "Banco de Talentos",
  "minha-vaga": "Minha Vaga",
  "empresa-account": "Perfil da Empresa",
  showvaga: "Vaga",
  "oxetechwork-inscricao": "Inscrição Work",
  ajuda: "Ajuda",
  "recuperar-acesso": "Recuperar Acesso",
  "inovation-map": "Mapa da Inovação",
  "politicas-privacidade": "Políticas de Privacidade",
  "oxetechlabdivulgacao": "Divulgação Lab",
};

// Rotas onde NÃO mostrar breadcrumbs por padrão
const DEFAULT_HIDDEN_ROUTES = ["/", "/login", "/logout", "/cadastro", "/empresa-cadastro", "/select-cadastro", "/forgot", "/sucess", "/dashboard", "/oxetechlab", "/oxedin", "/oxetechedu", "/oxetechlabdivulgacao", "/oxetrilhas", "/trilhas", "/oxe-tech-work", "/pibic-jr"];

// Verifica se um segmento parece ser um ID dinâmico (UUID, número, etc.)
function isDynamicSegment(segment) {
  // UUID
  if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(segment)) return true;
  // Número puro
  if (/^\d+$/.test(segment)) return true;
  return false;
}

function getLabel(segment, customLabels) {
  if (customLabels && customLabels[segment]) return customLabels[segment];
  if (ROUTE_LABELS[segment]) return ROUTE_LABELS[segment];
  // Fallback: capitalize e substituir hífens
  return segment
    .replace(/-/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

export default function AppBreadcrumbs({
  customLabels = {},
  hideOnRoutes = [],
  maxItems = 4,
}) {
  const location = useLocation();
  const history = useHistory();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const allHiddenRoutes = useMemo(
    () => [...DEFAULT_HIDDEN_ROUTES, ...hideOnRoutes],
    [hideOnRoutes]
  );

  const breadcrumbs = useMemo(() => {
    const segments = location.pathname.split("/").filter(Boolean);
    if (segments.length === 0) return [];

    const crumbs = [{ label: "Início", path: "/" }];
    let currentPath = "";

    for (let i = 0; i < segments.length; i++) {
      const seg = segments[i];
      currentPath += "/" + seg;

      // Pular segmentos dinâmicos (IDs) no breadcrumb
      if (isDynamicSegment(seg)) continue;

      const label = getLabel(seg, customLabels);
      crumbs.push({ label, path: currentPath });
    }

    return crumbs;
  }, [location.pathname, customLabels]);

  // Não mostrar em rotas ocultas
  const shouldHide = allHiddenRoutes.some(
    (r) => location.pathname === r || location.pathname.startsWith(r + "/")
  );
  if (shouldHide) return null;

  // Se só tem "Início", não faz sentido mostrar breadcrumb
  if (breadcrumbs.length <= 1) return null;

  // MOBILE: botão compacto "← Voltar para X"
  if (isMobile) {
    const parentCrumb = breadcrumbs[breadcrumbs.length - 2];
    if (!parentCrumb) return null;

    return (
      <Box sx={{ px: 2, pt: 1, pb: 0.5 }}>
        <Button
          size="small"
          startIcon={<ArrowBackIcon sx={{ fontSize: 18 }} />}
          onClick={() => history.push(parentCrumb.path)}
          sx={{
            color: "#666",
            textTransform: "none",
            fontSize: "0.8125rem",
            fontWeight: 500,
            minHeight: 36,
            px: 1,
            "&:hover": {
              backgroundColor: "rgba(0,0,0,0.04)",
            },
          }}
        >
          {parentCrumb.label}
        </Button>
      </Box>
    );
  }

  // DESKTOP: breadcrumb completo
  return (
    <Box sx={{ px: 3, pt: 1.5, pb: 0.5 }}>
      <Breadcrumbs
        separator={<NavigateNextIcon fontSize="small" sx={{ color: "#bbb" }} />}
        maxItems={maxItems}
        aria-label="breadcrumb"
        sx={{ fontSize: "0.875rem" }}
      >
        {breadcrumbs.map((crumb, index) => {
          const isLast = index === breadcrumbs.length - 1;

          if (isLast) {
            return (
              <Typography
                key={crumb.path}
                sx={{
                  fontSize: "0.875rem",
                  fontWeight: 600,
                  color: "#333",
                }}
              >
                {crumb.label}
              </Typography>
            );
          }

          return (
            <Link
              key={crumb.path}
              underline="hover"
              sx={{
                display: "flex",
                alignItems: "center",
                fontSize: "0.875rem",
                color: "#666",
                cursor: "pointer",
                "&:hover": { color: "#D46D0D" },
              }}
              onClick={(e) => {
                e.preventDefault();
                history.push(crumb.path);
              }}
            >
              {index === 0 && (
                <HomeIcon sx={{ mr: 0.5, fontSize: 18, color: "#999" }} />
              )}
              {crumb.label}
            </Link>
          );
        })}
      </Breadcrumbs>
    </Box>
  );
}